import { Component } from "react";

import { faAngleDoubleRight, faAngleRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PropTypes from "prop-types";
import { ButtonGroup, ButtonToolbar, FormSelect } from "react-bootstrap";
import FormGroup from "react-bootstrap/FormGroup";
import { injectIntl } from "react-intl";
import ObjectUtils, { coalesce } from "../../../utils/ObjectUtils";

const { getDeepProperty, findObjectIndexInArray } = ObjectUtils;

/**
 * Funding area component
 */
class FundingArea extends Component {
  componentDidMount() {
    const { fundingAreaList, target } = this.props;
    let fundingAreas = getDeepProperty(fundingAreaList, target);

    if (!fundingAreas) {
      this.props.reloadFundingAreaList(target);
    }
  }

  renderOptions(list, type, required, showForeignId) {
    const { intl, activeOnly } = this.props;

    const preparedList =
        type === "project" && showForeignId
        ? [...(list || [])].sort((a, b) =>
            (a.foreignId || "").localeCompare(b.foreignId || "", "de", {
                numeric: true,
                sensitivity: "base"
            })
            )
        : (list || []);

    return (
        <>
            <option value={required ? undefined : "all"}>
                {intl.formatMessage({
                id: required
                    ? "user.surveyView.dropdown.choose"
                    : "user.surveyBuilder." + type
                })}
            </option>

            {preparedList.map((e, i) =>
                (!activeOnly || !e.obsolete) && (
                <option key={e.foreignId || i} value={type === "project" ? e.foreignId : i}>
                    {type === "project" && showForeignId
                    ? [e.foreignId, e.name].filter(Boolean).join(" ")
                    : (e.name || e.foreignId)}
                </option>
                )
            )}
        </>
        );
    }

  render() {
    const {
      intl,
      fundingAreaList,
      selectedFundingArea,
      selectedProgram,
      selectedProgramInstance,
      selectedProject,
      target,
      preventProgramInstance,
      preventProjectChoice,
      faFilter,
      programInstanceRequired
    } = this.props;

    const fundingAreas = (getDeepProperty(fundingAreaList, target) || []).filter(faFilter || (() => true));
    const fundingAreaIdx = coalesce(
      findObjectIndexInArray(
        coalesce(fundingAreas, []),
        "id",
        coalesce(getDeepProperty(selectedFundingArea, target, "id"), "-1")
      ),
      -1
    );
    const currFundingArea = getDeepProperty(fundingAreas, fundingAreaIdx);

    const programs = getDeepProperty(currFundingArea, "programs");
    const programIdx = coalesce(
      findObjectIndexInArray(
        coalesce(programs, []),
        "id",
        coalesce(getDeepProperty(selectedProgram, target, "id"), "-1")
      ),
      -1
    );
    const currProgram = getDeepProperty(programs, programIdx);

    const programInstances = getDeepProperty(currProgram, "programInstances");
    if (programInstances && this.props.onProgramSelect && this.props.programRefreshNeeded) {
      this.props.onProgramSelect(programInstances);
    }

    const programInstanceIdx = coalesce(
      findObjectIndexInArray(
        coalesce(programInstances, []),
        "id",
        coalesce(getDeepProperty(selectedProgramInstance, target, "id"), "-1")
      ),
      -1
    );
    const currProgramInstance = getDeepProperty(programInstances, programInstanceIdx);

    const projects = getDeepProperty(currProgramInstance, "projects");


    return (
      <ButtonToolbar id="template-filter" className="bg-light p-2 w-100">
        <ButtonGroup className="gap-sm flex-wrap">
          <FormGroup className="m-0 flex-center gap-sm mb-3">
            <FontAwesomeIcon icon={faAngleDoubleRight} />
            <FormSelect
              placeholder={intl.formatMessage({ id: "user.surveyBuilder.fundingArea" })}
              title={currFundingArea && currFundingArea.name}
              onChange={(event) => {
                this.props.setPropertyValue("selectedFundingArea", target, fundingAreas[event.target.value]);
                this.props.setPropertyValue("selectedProgram", target, null);
                if (this.props.filterSelected) {
                  this.props.filterSelected(fundingAreas[event.target.value] ? fundingAreas[event.target.value] : null);
                }
                this.props.onSelectionStateChange &&
                  this.props.onSelectionStateChange("selectedFundingArea", fundingAreas[event.target.value]);
              }}
              value={fundingAreaIdx}
            >
              {this.renderOptions(fundingAreas, "fundingArea")}
            </FormSelect>
          </FormGroup>
          {programs && (
            <>
              <FormGroup className="m-0 flex-center gap-sm mb-3">
                <FontAwesomeIcon icon={faAngleRight} style={{ minWidth: ".75rem" }} />
                <FormSelect
                  className="d-inline-flex"
                  placeholder={intl.formatMessage({ id: "user.surveyBuilder.program.instance" })}
                  title={currProgram && currProgram.name}
                  onChange={(event) => {
                    this.props.setPropertyValue("selectedProgram", target, programs[event.target.value]);
                    this.props.setPropertyValue("selectedProgramInstance", target, null);
                    if (this.props.onProgramInstanceSelect) {
                      this.props.onProgramInstanceSelect(true);
                    }
                    if (this.props.filterSelected) {
                      this.props.filterSelected(
                        currFundingArea,
                        programs[event.target.value] ? programs[event.target.value] : null
                      );
                    }
                    this.props.onSelectionStateChange &&
                      this.props.onSelectionStateChange("selectedProgram", programs[event.target.value]);
                  }}
                  value={programIdx}
                >
                  {this.renderOptions(programs, "program")}
                </FormSelect>
              </FormGroup>
            </>
          )}
          {!preventProgramInstance && programInstances && (
            <>
              <FormGroup className="m-0 flex-center gap-sm mb-3">
                <FontAwesomeIcon icon={faAngleRight} style={{ minWidth: ".75rem" }} />
                <FormSelect
                  className="d-inline-flex"
                  placeholder={intl.formatMessage({ id: "user.surveyBuilder.program.instance" })}
                  title={currProgramInstance && currProgramInstance.name}
                  onChange={(event) => {
                    this.props.setPropertyValue(
                      "selectedProgramInstance",
                      target,
                      programInstances[event.target.value]
                    );
                    this.props.setPropertyValue("selectedProject", target, null);
                    if (this.props.filterSelected) {
                      this.props.filterSelected(
                        currFundingArea,
                        currProgram,
                        programInstances[event.target.value] ? programInstances[event.target.value] : null
                      );
                    }

                    this.props.onSelectionStateChange &&
                      this.props.onSelectionStateChange(
                        "selectedProgramInstance",
                        programInstances[event.target.value]
                      );
                  }}
                  value={programInstanceIdx}
                >
                  {programInstances && this.renderOptions(programInstances, "program.instance", programInstanceRequired)}
                </FormSelect>
              </FormGroup>
            </>
          )}
          {!preventProjectChoice && projects && (
            <>
              <FormGroup className="m-0 flex-center gap-sm mb-3">
                <FontAwesomeIcon icon={faAngleRight} style={{ minWidth: ".75rem" }} />
                <FormSelect
                  className="d-inline-flex"
                  placeholder={intl.formatMessage({ id: "user.surveyBuilder.project" })}
                  onChange={(event) => {
                    const newSelectedProject = ObjectUtils.findByObjectProp(projects, "foreignId", event.target.value);
                    this.props.setPropertyValue("selectedProject", target, newSelectedProject);
                    if (this.props.filterSelected) {
                      this.props.filterSelected(
                        currFundingArea,
                        currProgram,
                        currProgramInstance,
                        newSelectedProject
                      );
                    }
                    this.props.onSelectionStateChange &&
                      this.props.onSelectionStateChange("selectedProject", newSelectedProject);
                  }}
                  value={coalesce(getDeepProperty(selectedProject, target, "foreignId"), "-1")}
                >
                  {projects && this.renderOptions(projects, "project", false, "SAP"===currProgram?.category || "54836924"===currProgram?.foreignId)}
                </FormSelect>
              </FormGroup>
            </>
          )}
        </ButtonGroup>
      </ButtonToolbar>
    );
  }
}

FundingArea.propTypes = {
  fundingAreaList: PropTypes.object,
  selectedFundingArea: PropTypes.object,
  selectedProgram: PropTypes.object,
  selectedProject: PropTypes.object,
  selectedProgramInstance: PropTypes.object,
  intl: PropTypes.object,
  target: PropTypes.string.isRequired,
  filterSelected: PropTypes.func,
  preventProjectChoice: PropTypes.bool,
  preventProgramInstance: PropTypes.bool,
  activeOnly: PropTypes.bool,
  reloadFundingAreaList: PropTypes.func,
  setPropertyValue: PropTypes.func,
  onSelectionStateChange: PropTypes.func,
  programInstanceRequired: PropTypes.bool,
};

export default injectIntl(FundingArea);
